/// <amd-module name="Build/Designer/WizardList" />
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { css } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Image } from "VSSUI/Image";
import { IListItemDetails, IListRow, List, ListItem } from "VSSUI/List";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IWizardListItem {
    name: string;
    description: string;
    iconClass?: string;
    iconName?: string;
    iconUrl?: string;
    iconAltText?: string;
    labels?: string[];
    isFavorite?: boolean;
    renderDescription?: () => JSX.Element;
}

export interface WizardListProps {
    wizardListItems: (IWizardListItem | IObservableValue<undefined>)[];
    loadMore?: () => void;
    onClick: (event: React.SyntheticEvent<HTMLElement>, row: IListRow<IWizardListItem>) => void;
}

export function WizardList(props: WizardListProps): JSX.Element {
    const renderIcon = (item: IWizardListItem): JSX.Element => {
        return item.iconUrl ? (
            <Image className={css("list-image", item.iconClass)} src={item.iconUrl} alt={item.iconAltText || ""} />
        ) : (
            <Icon className={css("list-icon", item.iconClass)} iconName={item.iconName} />
        );
    };
    const renderLabels = (item: IWizardListItem): JSX.Element | undefined => {
        if (!item || !item.labels) {
            return;
        }
        return (
            <PillGroup className="flex-row flex-noshrink">
                {item.labels.map((label, index) => (
                    <Pill className={"list-item-label"} size={PillSize.compact} key={index} excludeTabStop={true} excludeFocusZone={true}>
                        {label}
                    </Pill>
                ))}
            </PillGroup>
        );
    };
    const renderRow = (index: number, item: IWizardListItem, details: IListItemDetails<IWizardListItem>, key?: string): JSX.Element => {
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <div className="flex-row flex-grow cursor-pointer scroll-hidden">
                    {renderIcon(item)}
                    <div className="list-content flex-column flex-grow scroll-hidden">
                        <div className="flex-row flex-grow flex-center">
                            <span className="body-m text-ellipsis">{item.name}</span>
                            {renderLabels(item)}
                        </div>
                        {!!item.renderDescription ? item.renderDescription() : <span className="body-s secondary-text">{item.description}</span>}
                    </div>
                    {item.isFavorite && <Icon className="favorite-icon" iconName={"FavoriteStarFill"} />}
                </div>
            </ListItem>
        );
    };

    const itemProvider = new ArrayItemProvider(props.wizardListItems);
    return (
        <List
            className="wizard-list"
            itemProvider={itemProvider}
            onActivate={props.onClick}
            singleClickActivation={true}
            renderRow={renderRow}
            renderLoadingRow={(rowIndex: number, details: IListItemDetails<IWizardListItem>) => {
                props.loadMore && props.loadMore();
                return (
                    <ListItem className="bolt-list-row-loading" details={details} index={rowIndex}>
                        <div className="flex-grow margin-16">
                            <Spinner size={SpinnerSize.large} />
                        </div>
                    </ListItem>
                );
            }}
            width={"100%"}
        />
    );
}
